import React from 'react';
import { useState } from "react";
import {
  Grid,
  TextField,
  Button,
  Typography,
  Checkbox,
  Stack,
} from "@mui/material";
import AlertBar from "../utils/AlertBar";
import { useTranslation } from "react-i18next";
import { useTitle } from "../utils/useTitle";
import { useAnalyticsEventTracker } from "../utils/useAnalyticsEventTracker";

/**
 * News Letter Subscription Component
 * this component displays the fields to ask for:
 * - first name
 * - last name
 * - email
 * - company
 * - checkbox for agreeing to terms
 * - subscribe button
 * 
 * @returns {JSX.Element} - News Letter Subscription Component
 */
export const NewslettersSubscription = () => {
  useTitle("Subscribe to our Newsletters");
  const eventTracker = useAnalyticsEventTracker("Newsletter");
  const { t } = useTranslation();
  // these are the fields that are currently asked for, can always add more to the array if properly set up with the locale
  const newsletterFields = ["firstName", "lastName", "email", "company"];

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    company: "",
    agreeTerms: false,
  });

  const [errors, setErrors] = useState({
    firstName: false,
    lastName: false,
    email: false,
    company: false,
    agreeTerms: false,
  });

  const [errorMessage, setErrorMessage] = useState("");

  const [isSuccess, setIsSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  function handleIsSuccessState() {
    setIsSuccess(false);
  }

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Use the appropriate value based on the input type
    const inputValue = type === "checkbox" ? checked : value;

    setFormData((prevData) => ({
      ...prevData,
      [name]: inputValue,
    }));

    // Clear the error for the current field when the user starts typing
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: false,
    }));
  };

  // this displays the first name and last name
  const displayUsernameInput = newsletterFields.slice(0, 2).map((field) => {
    return (
      <Grid item xs={6} key={field}>
        <InputTemplate 
          t={t}
          fieldName={field}
          formData={formData}
          errors={errors}
          handleInputChange={handleInputChange}
        />
      </Grid>
    );
  });

  // this displays the additional inputs such as email, company and other fields can be added later on
  const additionalInformationInput = newsletterFields.slice(2, newsletterFields.length).map( (field) => {
    return (
      <Grid item>
        <InputTemplate 
          t={t}
          fieldName={field}
          formData={formData}
          errors={errors}
          handleInputChange={handleInputChange}
        />
      </Grid>
    );
  }); 

  return (
    <>
      <Grid container>
        <Grid item xs={6} container justify="center" alignItems="center">
          <Grid
            container
            direction="column"
            spacing={3}
            style={{ maxWidth: "800px", padding: "20px", marginLeft: "70px" }}
          >
            <Grid item>
              <h1>{t("newsletterSubscription.header")}</h1>
              <Grid container spacing={2}>
                {displayUsernameInput}
              </Grid>
            </Grid>
            
            {additionalInformationInput}
            
            <AgreeTermsCheckbox t={t} formData={formData} errors={errors} handleInputChange={handleInputChange} />

            <SubscribeButton 
              t={t} 
              eventTracker={eventTracker} 
              formData={formData} 
              setErrors={setErrors} 
              setErrorMessage={setErrorMessage} 
              setIsSuccess={setIsSuccess} 
              setSuccessMessage={setSuccessMessage} 
              setFormData={setFormData} 
            />
          </Grid>
        </Grid>

        <Banner />
      </Grid>
      ;
      <AlertBar
        open={isSuccess}
        message={successMessage}
        severity={"success"}
        onClose={handleIsSuccessState}
      />
      <AlertBar
        open={Object?.keys(errors).some(
          (key) => key !== "agreeTerms" && errors[key]
        )}
        message={errorMessage}
        severity={"error"}
        onClose={() => setErrors({})}
      />
    </>
  );
};

/**
 * Input Template Component
 * this component is a template for displaying the inputs onto the news letter
 * 
 * @param {Function} t
 * @param {String} fieldName
 * @param {Object} formData
 * @param {Object} errors 
 * @param {Function} handleInputChange
 * @returns {JSX.Element} Input Template Component 
 */
function InputTemplate({ t, fieldName, formData, errors, handleInputChange }) {
  return (
    <TextField
      label={t(`newsletterSubscription.${fieldName}`)}
      variant="outlined"
      fullWidth
      required
      value={formData[fieldName]}
      onChange={handleInputChange}
      name={fieldName}
      error={errors[fieldName]}
      helperText={
        errors[fieldName] &&
        t(`newsletterSubscription.requiredMsg.${fieldName}`)
      }
    />
  );
}

/**
 * Agree Terms Checkbox Component
 * this component displays the checkbox for asking the user to agree to terms
 * 
 * @param {Function} t
 * @param {Object} formData
 * @param {Object} errors
 * @param {Function} handleInputChange
 * @returns {JSX.Element} Agree Terms Checkbox Component
 */
function AgreeTermsCheckbox({ t, formData, errors, handleInputChange }) {
  return (
    <Grid item>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Checkbox
          checked={formData.agreeTerms}
          onChange={handleInputChange}
          name="agreeTerms"
          color="success"
        />
        <Typography variant="body2" color="textSecondary">
          {t("newsletterSubscription.agreeTerms")}
        </Typography>
      </Stack>
      {errors.agreeTerms && (
        <Typography variant="caption" color="error">
          {t("newsletterSubscription.requiredMsg.agreeTerms")}
        </Typography>
      )}
    </Grid>
  );
}

/**
 * @component Banner Component
 * this component displays the banner image
 * 
 * @returns {JSX.Element} Banner Component
 */
function Banner() {
  return (
    <Grid item xs={6}>
      <img
        src="assets/images/banner1.jpg"
        alt="Signup"
        style={{
          width: "100%",
          height: "100vh",
          objectFit: "cover",
        }}
      />
    </Grid>
  );
}

/**
 * Subscribe Button Component
 * this component renders the subscribe button and handles all of the form errors and such
 * 
 * @param {Function} t 
 * @param {Function} eventTracker 
 * @param {Object} formData 
 * @param {Function} setErrors 
 * @param {Function} setErrorMessage 
 * @param {Function} setIsSuccess  
 * @param {Function} setSuccessMessage 
 * @param {Function} setFormData 
 * @returns {JSX.Element} Subscribe Button Component
 */
function SubscribeButton({ t, eventTracker, formData, setErrors, setErrorMessage, setIsSuccess, setSuccessMessage, setFormData }) {
  const handleFormSubmit = async () => {
    // Validate form fields
    const formErrors = {};
    Object?.keys(formData).forEach((key) => {
      if (!formData[key]) {
        formErrors[key] = true;
      }
    });

    if (!formData.agreeTerms) {
      formErrors.agreeTerms = true;
    }

    if (Object?.keys(formErrors).length > 0) {
      console.log({ formErrors });
      // If there are errors, set the state to show errors
      setErrors(formErrors);
      setErrorMessage(t("newsletterSubscription.alertMsg.error1"));
      return;
    }

    try {
      setIsSuccess(true);
      setSuccessMessage(t("newsletterSubscription.alertMsg.success"));
      setFormData({});
      eventTracker("newsletter_subscription");
    } catch (error) {
      console.error("Error:", error);
      setErrors({ error: true });
      setErrorMessage(t("newsletterSubscription.alertMsg.error2"));
    } finally {
      // Reset form data and errors after successful submission or error
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        company: "",
        agreeTerms: false,
      });
    }
  };

  return (
    <Grid item>
      <Button
        variant="contained"
        color="success"
        fullWidth
        size="large"
        style={{ height: "55px" }}
        onClick={handleFormSubmit}
      >
        {t("newsletterSubscription.btnText")}
      </Button>
    </Grid>
  );
}